import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";

const strongPasswordRegex =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

export default function ChangePasswordModal({
  open,
  onClose,
  onSubmit,
  loading,
  error,
  success,
}) {
  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [show, setShow] = useState({ old: false, new: false, confirm: false });
  const [touched, setTouched] = useState({});
  const [internalSuccess, setInternalSuccess] = useState(false);

  React.useEffect(() => {
    if (success) {
      setInternalSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1200);
    }
  }, [success]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setTouched({ ...touched, [e.target.name]: true });
  };

  const handleShow = (field) => setShow((s) => ({ ...s, [field]: !s[field] }));

  const validate = () => {
    const errors = {};
    if (!form.oldPassword) errors.oldPassword = "Current password is required";
    if (!form.newPassword) errors.newPassword = "New password is required";
    else if (!strongPasswordRegex.test(form.newPassword))
      errors.newPassword =
        "Password must be at least 8 characters, include uppercase, lowercase, number, and special character";
    if (!form.confirmPassword)
      errors.confirmPassword = "Confirm your new password";
    else if (form.newPassword !== form.confirmPassword)
      errors.confirmPassword = "Passwords do not match";
    return errors;
  };

  const errors = validate();
  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ oldPassword: true, newPassword: true, confirmPassword: true });
    if (isValid && onSubmit) {
      onSubmit({
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
      });
    }
  };

  const handleClose = () => {
    setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setTouched({});
    setInternalSuccess(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown={false}
      disableBackdropClick={true}
      aria-labelledby="change-password-modal"
    >
      <Box
        sx={{
          p: 4,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
          minWidth: 350,
          maxWidth: 400,
          mx: "auto",
          mt: "10vh",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          position: "relative",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          Change Password
        </Typography>
        {success && (
          <Alert severity="success">Password changed successfully!</Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <form onSubmit={handleSubmit} autoComplete="off">
          <TextField
            label="Current Password"
            name="oldPassword"
            type={show.old ? "text" : "password"}
            value={form.oldPassword}
            onChange={handleChange}
            onBlur={() => setTouched((t) => ({ ...t, oldPassword: true }))}
            error={!!touched.oldPassword && !!errors.oldPassword}
            helperText={touched.oldPassword && errors.oldPassword}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleShow("old")}
                    edge="end"
                    size="small"
                  >
                    {show.old ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="New Password"
            name="newPassword"
            type={show.new ? "text" : "password"}
            value={form.newPassword}
            onChange={handleChange}
            onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
            error={!!touched.newPassword && !!errors.newPassword}
            helperText={touched.newPassword && errors.newPassword}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleShow("new")}
                    edge="end"
                    size="small"
                  >
                    {show.new ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm New Password"
            name="confirmPassword"
            type={show.confirm ? "text" : "password"}
            value={form.confirmPassword}
            onChange={handleChange}
            onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
            error={!!touched.confirmPassword && !!errors.confirmPassword}
            helperText={touched.confirmPassword && errors.confirmPassword}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleShow("confirm")}
                    edge="end"
                    size="small"
                  >
                    {show.confirm ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading || !isValid}
            startIcon={loading && <CircularProgress size={18} />}
          >
            Change Password
          </Button>
        </form>
      </Box>
    </Modal>
  );
}
